/* =========================================================================
   ClueVine — marketing content (assets + copy + plan)
   The data behind the admin Marketing tab: every artifact we made, paste-ready
   launch copy, and a researched, channel-by-channel go-to-market plan tuned to
   a daily word game. Plain data so it's easy to edit without touching the UI.
   ========================================================================= */

export interface MarketingAsset {
  name: string;
  src: string;
  use: string;
}

/** Served assets (public/) — shown live in the dashboard. */
export const MARKETING_ASSETS: MarketingAsset[] = [
  { name: 'Social card (OG)', src: '/og.png', use: 'Link unfurls everywhere — PH, X, Reddit, iMessage, Slack.' },
  { name: 'App icon', src: '/icon-512.png', use: 'PWA install + the home-screen icon (an install is a selling point).' },
  { name: 'Launch GIF · portrait', src: '/marketing/launch.gif', use: 'Mobile feeds, Reddit posts, the README hero.' },
  { name: 'Launch GIF · landscape', src: '/marketing/launch-landscape.gif', use: 'Product Hunt gallery, X/Twitter, desktop embeds.' },
  { name: 'Modes showcase', src: '/marketing/modes-showcase.gif', use: '“What is it” at a glance — PH gallery lead, a pinned tweet.' },
];

export const MODE_CLIP_IDS = [
  'cipher', 'forge', 'tangle', 'trellis', 'branches', 'bramble', 'bloom', 'petal', 'bouquet',
] as const;

export interface CopyBlock {
  label: string;
  text: string;
}

export const MARKETING_COPY: CopyBlock[] = [
  {
    label: 'Tagline',
    text: 'Eleven daily word puzzles, one growing vine.',
  },
  {
    label: 'One-liner',
    text:
      'ClueVine is a cozy daily word game — eleven puzzle modes on one engine, with your streak drawn as a growing vine. Free, works offline, installs to your phone, no login.',
  },
  {
    label: 'Product Hunt — description',
    text:
      'ClueVine is a daily word game for people who like their puzzles cozy and their streaks growing. Eleven modes share one engine, so there is always a fresh way to play: hidden-word deduction, a seven-letter builder, anagrams, a word ladder, a word chain, a word search, fill-the-vowels, hangman, four-at-once, a trace-a-path hunt, and a mini word-grid.\n\nDaily + endless play, a streak drawn as a living vine, a mode of the day, hints, difficulty levels, a daily calendar, achievements, a color-blind-safe palette, and dark mode. It is an installable PWA that works fully offline, with no account and no backend. Free to play, always.',
  },
  {
    label: 'r/WordGames post',
    text:
      'I made a free daily word game with 11 different puzzle modes — would love feedback.\n\nI got tired of a separate app for every kind of word puzzle, so I built one with eleven modes on a shared daily + streak system: deduction, a letter-set builder, anagrams, a word ladder, a word chain, a word search, fill-the-vowels, hangman, four-at-once, a trace-a-path hunt, and a mini word-grid.\n\nIt is free, works offline, no account. Color-blind-safe clues and full keyboard play if that matters to you. [link] — tell me which mode is the best/worst and whether the difficulty feels right. Brand new, so anything broken I want to hear it.',
  },
  {
    label: 'Show HN',
    text:
      'Show HN: ClueVine – a daily word game, 11 modes, offline PWA, no backend\n\nI built a daily word game with eleven puzzle modes on a single PuzzleMode interface + a registry, so every mode gets daily/endless play, deterministic seeds, streaks, hints, scoring, stats, and accessibility for free. Adding a mode touches only its own folder plus one register call.\n\nReact/TypeScript, fully offline (a service worker precaches the app shell and a ~32k-word dictionary), no backend, no accounts. Daily puzzles are deterministic from the date via a dependency-free PRNG. Feedback welcome, especially on difficulty and the color-blind-safe palette. [link]',
  },
  {
    label: 'X / Twitter thread',
    text:
      '1/ I built ClueVine 🌿 a cozy daily word game. Eleven puzzle modes, one vine that grows a leaf for every day of your streak. Free, offline, no login. [link]\n\n2/ Why eleven modes? Because “one word game per app” is silly. Cipher, Forge, Tangle, Trellis, Branches, Bramble, Bloom, Petal, Bouquet, Thorn, Lattice — all on one daily/streak system, with a mode of the day.\n\n3/ Installable PWA. Add to home screen, play on the subway with no signal. The whole 32k-word dictionary is cached locally.\n\n4/ Accessibility wasn’t an afterthought: color-blind-safe clues with patterns, high contrast, reduced motion, full keyboard play.\n\n5/ Free, always. A $3 supporter unlock removes the one ad and adds theme skins if you want to chip in. Play today’s: [link]',
  },
];

export interface PlanSection {
  phase: string;
  title: string;
  points: string[];
}

export const MARKETING_PLAN: PlanSection[] = [
  {
    phase: 'Positioning',
    title: 'What it is, in one breath',
    points: [
      'A cozy daily word game with real variety: eleven modes on one engine, a growing-vine streak, a mode of the day.',
      'Edge vs Wordle/NYT: variety + the vine motif + genuine accessibility + fully offline, installable, no login.',
      'Audience: the Wordle / NYT Games crowd, puzzle hobbyists, indie-game and PWA/tech folks.',
    ],
  },
  {
    phase: 'Phase 0',
    title: 'Pre-launch (as the domain goes live)',
    points: [
      'Point cluevine.com, deploy (DEPLOY.md), then play every mode on the live site — confirm no console errors.',
      'Verify links unfurl with og.png (paste the URL into iMessage/Slack/X) and the daily rolls over at local midnight.',
      'Stand up a feedback channel (a form, your email, or a Discord) and link it in Settings/footer.',
      'Seed 5–10 friends so day one is not an empty room and you get first streaks + feedback.',
      'Keep ads OFF (Monetization tab). A clean, free launch builds the habit; ads on a 50-visitor site earn nothing and read as spam.',
    ],
  },
  {
    phase: 'Phase 1',
    title: 'Soft launch — week 1, build a habit base',
    points: [
      'Post to ONE community first: r/WordGames (most relevant). Use the prepared post; reply to every comment like a person.',
      'Goal is your first 100–1,000 players and real feedback, not virality. Iterate on what they say.',
      'Watch which modes get played and where people bounce; tune difficulty if a mode feels unfair.',
    ],
  },
  {
    phase: 'Phase 2',
    title: 'Launch beats — week 1–2',
    points: [
      'Product Hunt (Tue–Thu): use the PH copy + a gallery of the showcase GIF, a Cipher mid-game, the result card, and the landscape GIF. Post the maker’s first comment; rally your seed list to engage early (PH rewards early velocity).',
      'Show HN (morning ET): lead with the architecture — one engine, eleven modes, offline PWA, deterministic seeds, no backend. HN loves the build story.',
      'Expand on Reddit: r/SideProject, r/webgames, r/playmygame, and r/InternetIsBeautiful (high bar — must be genuinely free + lovely). Read each sub’s rules; tailor each post; never crosspost-spam.',
    ],
  },
  {
    phase: 'Phase 3',
    title: 'Sustain — week 2+, retention + the share loop',
    points: [
      'The share-result image is the organic engine (Wordle’s lesson): the share text links back and unfurls with og.png. Make sharing a one-tap habit.',
      'Mode of the day gives a daily reason to return and a daily social hook (“Today’s ClueVine is Thorn”).',
      'Short vertical clips of single modes (the per-mode GIFs) on TikTok / Reels / Shorts — word games do well there.',
      'X/Twitter: the prepared thread + the landscape GIF; engage word-game accounts and reply to quote-shares.',
      'SEO long-tail: “free daily word game”, “Wordle alternatives”, “word puzzle game” — a clear landing description helps.',
    ],
  },
  {
    phase: 'Money',
    title: 'Monetization sequencing (habit first)',
    points: [
      'Weeks 1–3: nothing. Get people hooked on the daily.',
      'Weeks 2–3, once you have returning players: turn on the Supporter unlock + tip jar (Monetization tab) — set the Stripe + Ko-fi links.',
      'Later, with real traffic: enable the one ad (Monetization tab). Word games monetize slowly — habit first, money later.',
    ],
  },
  {
    phase: 'Measure',
    title: 'What to watch',
    points: [
      'Returning % and streak-length distribution (are people forming the habit?).',
      'Share rate and install rate (the two growth levers).',
      'Which modes get played most/least, and where first-session players drop.',
      'Add a privacy-friendly analytics snippet (e.g. Plausible) when ready — no cross-user tracking is built in by design.',
    ],
  },
];
